// What one tool call of the agent did, to evaluate a turn: for a write, the cells as they were and what was written;
// for anything else the arguments; then the whole result. The before-images come from the add-in's undo journal.
import { useTranslation } from 'react-i18next';
import { Badge, type Tone } from '../../components/ui';
import type { RangeSnapshot, TranscriptToolCall } from '../../types/api';

const MAX_ROWS = 40;
const MAX_COLS = 16;

function callTone(status: string): Tone {
  return status === 'ok' ? 'good' : status === 'error' ? 'bad' : status === 'cancelled' ? 'warn' : 'neutral';
}

// ---- A1 addresses ----

function colNumber(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

function colLetters(n: number): string {
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** `$C$52` → {col: 3, row: 52}; null when it is not a cell. */
export function parseCell(address: string): { col: number; row: number } | null {
  const m = /^\$?([A-Za-z]{1,3})\$?(\d+)$/.exec(address.trim().replace(/^.*!/, ''));
  return m ? { col: colNumber(m[1]), row: Number(m[2]) } : null;
}

/** `D52:F52` (or a single cell) → its corners. */
export function parseRange(address: string): { c0: number; r0: number; c1: number; r1: number } | null {
  const [a, b] = address.replace(/^.*!/, '').split(':');
  const start = parseCell(a ?? '');
  const end = b ? parseCell(b) : start;
  return start && end ? { c0: start.col, r0: start.row, c1: end.col, r1: end.row } : null;
}

/** The formula (or value) a snapshot held at one cell, if the snapshot covers it. */
export function beforeAt(snapshots: RangeSnapshot[] | undefined, col: number, row: number): unknown {
  for (const s of snapshots ?? []) {
    const r = parseRange(s.address);
    if (!r || col < r.c0 || col > r.c1 || row < r.r0 || row > r.r1) continue;
    const formula = s.formulas?.[row - r.r0]?.[col - r.c0];
    return formula !== undefined && formula !== '' ? formula : s.values?.[row - r.r0]?.[col - r.c0];
  }
  return undefined;
}

function show(v: unknown): string {
  if (v === undefined) return '';
  if (v === null || v === '') return '∅';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

// ---- views ----

function Json({ value }: { value: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto rounded-sm border border-line-soft bg-white p-2 font-mono text-[11px] leading-relaxed text-ink">
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-faint">{title}</div>
      {children}
    </div>
  );
}

/** excel_write_cells: one row per cell, before → written. */
function WriteCellsView({ call }: { call: TranscriptToolCall }) {
  const { t } = useTranslation();
  const cells = (call.input.cells ?? {}) as Record<string, unknown>;
  // A long formula must scroll inside the call, never widen the transcript column.
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full table-fixed border-collapse font-mono text-[11px]" data-testid="write-cells">
        <thead>
          <tr className="text-left text-faint">
            <th className="w-[15%] border-b border-line-soft py-1 pr-3 font-normal">{t('quality.address')}</th>
            <th className="border-b border-line-soft py-1 pr-3 font-normal">{t('quality.before')}</th>
            <th className="border-b border-line-soft py-1 font-normal">{t('quality.written')}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(cells).map(([address, value]) => {
            const at = parseCell(address);
            const before = at ? beforeAt(call.before, at.col, at.row) : undefined;
            return (
              <tr key={address} className="align-top">
                <td className="py-0.5 pr-3 text-muted">{address}</td>
                <td className="break-all py-0.5 pr-3 text-faint">{call.before ? show(before) : '?'}</td>
                <td className="break-all py-0.5 text-ink">{show(value)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Grid({ rows, c0, r0, testId }: { rows: unknown[][]; c0: number; r0: number; testId?: string }) {
  const clipped = rows.slice(0, MAX_ROWS).map((r) => r.slice(0, MAX_COLS));
  const width = Math.max(0, ...clipped.map((r) => r.length));
  return (
    <div className="max-h-72 overflow-auto rounded-sm border border-line-soft bg-white">
      <table className="border-collapse font-mono text-[10.5px]" data-testid={testId}>
        <thead>
          <tr>
            <th />
            {Array.from({ length: width }, (_, i) => (
              <th key={i} className="border-b border-line-soft px-2 py-0.5 font-normal text-faint">{colLetters(c0 + i)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {clipped.map((row, i) => (
            <tr key={i}>
              <td className="pr-2 text-right text-faint">{r0 + i}</td>
              {row.map((v, j) => (
                <td key={j} className="whitespace-nowrap border-l border-line-row px-2 py-0.5 text-ink">{show(v)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(rows.length > MAX_ROWS || rows.some((r) => r.length > MAX_COLS)) && <div className="px-2 py-1 text-[10.5px] text-faint">…</div>}
    </div>
  );
}

/** excel_write_formulas: the range, the pattern or the formulas, and the formulas the range held before. */
function WriteFormulasView({ call }: { call: TranscriptToolCall }) {
  const { t } = useTranslation();
  const address = String(call.input.address ?? '');
  const range = parseRange(address);
  const pattern = call.input.formula_r1c1 as string | undefined;
  const formulas = call.input.formulas as unknown[][] | undefined;
  const snapshot = call.before?.[0];
  const snapRange = snapshot ? parseRange(snapshot.address) : null;
  return (
    <div className="flex flex-col gap-2">
      <div className="font-mono text-[11px] text-ink">
        {t('quality.range')}: {call.input.sheet as string}!{address}
        {range && ` (${(range.r1 - range.r0 + 1) * (range.c1 - range.c0 + 1)} ${t('quality.cells')})`}
      </div>
      {pattern && (
        <Section title={t('quality.pattern')}>
          <code className="break-all rounded-sm bg-white px-2 py-1 font-mono text-[11.5px] text-ink" data-testid="r1c1">{pattern}</code>
        </Section>
      )}
      {formulas && range && (
        <Section title={t('quality.written')}>
          <Grid rows={formulas} c0={range.c0} r0={range.r0} testId="written-grid" />
        </Section>
      )}
      {snapshot && snapRange && (
        <Section title={t('quality.before')}>
          <Grid rows={snapshot.formulas ?? snapshot.values ?? []} c0={snapRange.c0} r0={snapRange.r0} testId="before-grid" />
        </Section>
      )}
    </div>
  );
}

function ResultView({ result }: { result: unknown }) {
  const { t } = useTranslation();
  if (result === undefined || result === null) return <p className="text-[11.5px] text-faint">{t('quality.noResult')}</p>;
  const cut = typeof result === 'object' && result !== null && (result as { truncated?: boolean }).truncated;
  if (cut) {
    const r = result as { chars: number; head: string };
    return (
      <>
        <p className="text-[11px] text-warn">{t('quality.truncated', { chars: r.chars })}</p>
        <Json value={r.head} />
      </>
    );
  }
  return <Json value={result} />;
}

function target(call: TranscriptToolCall): string {
  if (call.changes) return `${call.changes.sheet}!${call.changes.address}`;
  const sheet = call.input.sheet as string | undefined;
  const address = call.input.address as string | undefined;
  if (sheet && address) return `${sheet}!${address}`;
  const text = JSON.stringify(call.input);
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}

export function ToolCallDetail({ call, open }: { call: TranscriptToolCall; open: boolean }) {
  const { t } = useTranslation();
  const isWrite = call.tool_name === 'excel_write_cells' || call.tool_name === 'excel_write_formulas';
  return (
    <details open={open} className="rounded-sm border border-line-soft bg-code font-mono text-[11px] text-muted" data-testid={`call-${call.tool_call_id}`}>
      <summary className="flex cursor-pointer items-center gap-2 px-2 py-1">
        <Badge tone={callTone(call.status)}>{call.status}</Badge>
        <span className="text-ink">{call.tool_name}</span>
        <span className="truncate">{target(call)}</span>
        {call.changes && <span className="shrink-0 text-faint">{t('quality.cellsChanged', { count: call.changes.cells })}</span>}
        {call.undone && <Badge tone="warn">{t('quality.undone')}</Badge>}
      </summary>
      <div className="flex flex-col gap-3 border-t border-line-soft px-3 py-2 font-sans">
        {call.error && (
          <div className="text-[12px] text-danger">
            {call.error}
            {call.hint && <div className="text-muted">{call.hint}</div>}
          </div>
        )}
        {call.tool_name === 'excel_write_cells' && (
          <Section title={t('quality.whatWasWritten')}>
            <WriteCellsView call={call} />
          </Section>
        )}
        {call.tool_name === 'excel_write_formulas' && (
          <Section title={t('quality.whatWasWritten')}>
            <WriteFormulasView call={call} />
          </Section>
        )}
        {isWrite && !call.before && call.status === 'ok' && <p className="text-[11px] text-faint">{t('quality.noBefore')}</p>}
        <Section title={t('quality.arguments')}>
          <Json value={call.input} />
        </Section>
        <Section title={t('quality.result')}>
          <ResultView result={call.result} />
        </Section>
      </div>
    </details>
  );
}
